import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ConfigService } from 'src/app/core/services/config.service';
import { ClaimDocumentationDocumentInfo } from 'src/app/feature-module/documents/models/ClaimDocumentationDocumentInfo';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FileInfo } from '@progress/kendo-angular-upload';

import { ClaimMovieDocumentInfo } from '../models/ClaimMovieDocumentInfo';
import { ClaimPhotoDocumentInfo } from '../models/ClaimPhotoDocumentInfo';
import { DocumentDetailedInfoSearchResultItem } from '../models/DocumentDetailedInfoSearchResultItem';
import { DocumentInfo } from '../models/DocumentInfo';
import { DocumentInfoSearchResultItem } from '../models/DocumentInfoSearchResultItem';
import { DocumentLinkInfo } from '../models/DocumentLinkInfo';
import { ExportDocumentItem } from '../models/ExportDocumentItem';
import { PhotoWithThumb } from '../models/PhotoWithThumb';
import { ProofDocument } from '../models/ProofDocument';
import { SearchDocumentInfoCriteria } from '../models/SearchDocumentInfoCriteria';
import { SearchDocumentsByIdsCriteria } from '../models/SearchDocumentsByIdsCriteria';
import { SentDocumentInfo } from '../models/SentDocumentInfo';
import { ExportDocumentsAsLinkRequest } from '../models/ExportDocumentsAsLinkRequest';
import { ExportDocumentsData } from '../models/ExportDocumentData';
import { LocalizationManagerService } from 'src/app/shared/services/localization-manager.service';
import { ChangePhotosDocumentTypeRequest } from '../models/ChangePhotosDocumentTypeRequest';
import { SearchSharedClaimDocumentLinkCriteria } from '../models/SearchSharedClaimDocumentLinkCriteria';
import { SharedClaimDocumentLinkSearchResultItem } from '../models/SharedClaimDocumentLinkSearchResultItem';
import { SharedClaimDocumentLink } from '../models/SharedClaimDocumentLink';

@Injectable({
  providedIn: 'root'
})
export class DocumentService {

  constructor(private http: HttpClient,
              private configService: ConfigService,
              private localizationManagerService: LocalizationManagerService) {
  }

  public uploadDocument( formData: FormData) {
    return this.http.post(this.configService.PortalWebApiUrl + 'Document', formData);
  }

  public uploadAttachedDocuments( filesToAttach: FileInfo[], documentTypeId: number, referenceId: string,referenceType:number, 
    claimIdentifier:string, documentKindId:number, description=''  ) {
    
    filesToAttach.forEach((file: FileInfo) => {
      const formData = new FormData();
      formData.append('files', file.rawFile as Blob, file.name);
      formData.append("documentTypeId", documentTypeId.toString());
      formData.append("referenceId", referenceId);
      formData.append("referenceType", referenceType.toString());
      formData.append("documentKindId", documentKindId.toString());
      
      if(claimIdentifier){
        formData.append("claimIdentifier", claimIdentifier);
      }

      if(description){
        formData.append("description", description);
      }

      this.uploadDocument(formData).subscribe( tap() );
    });

   
  }
  public rotateDocument( documentInfoId: string, degrees: number) : Observable<PhotoWithThumb>{
    return this.http.put<PhotoWithThumb>(`${this.configService.PortalWebApiUrl}Document/Rotate/${documentInfoId}`, degrees);
  }

  public getDocument( documentInfoId: string) : Observable<DocumentInfo> {
    return this.http.get<DocumentInfo>(`${this.configService.PortalWebApiUrl}Document/${documentInfoId}`);
  }

  public downloadDocument( documentInfoId: string) {
    return this.http.get(`${this.configService.PortalWebApiUrl}Document/Download/${documentInfoId}`,
     { reportProgress: true, responseType: 'blob'} );
  }

  public storeProofDocument(proofDocument:ProofDocument) : Observable<number> {
    return this.http.post<number>(
      this.configService.PortalWebApiUrl + 'Document/ProofDocument/store', proofDocument);
  }

  public updateDocumentInfo(documentInfo:DocumentInfo) : Observable<number> {
    return this.http.post<number>(
      this.configService.PortalWebApiUrl + 'Document/updateDocumentInfo', documentInfo);
  }

  public changeDocumentPhotosType(docs:ChangePhotosDocumentTypeRequest) : Observable<number> {
    return this.http.post<number>(
      this.configService.PortalWebApiUrl + 'Document/changePhotosDocumentType', docs);
  }

  public getProofDocuments(id: string): Observable<ProofDocument[]> {
    return this.http.get<ProofDocument[]> (`${this.configService.PortalWebApiUrl}Document/ProofDocument/list/${id}`)
  }

  public removeProofDocument(claimIdentifier:string,proofDocumentId:number ) {
    return this.http.delete(
      this.configService.PortalWebApiUrl +  `Document/ProofDocument/${claimIdentifier}/${proofDocumentId}`);
  }

  public removeDocument(documentInfoId:string ) {
    return this.http.delete(
      this.configService.PortalWebApiUrl +  `Document/${documentInfoId}`);
  }

  public searchDocumentInfo(criteria:SearchDocumentInfoCriteria) : Observable<DocumentInfoSearchResultItem[]> {
    return this.http.post<DocumentInfoSearchResultItem[]>(
      this.configService.PortalWebApiUrl + 'Document/search', criteria);
  }

  public getClaimDocumentation(claimIdentifier:string ): Observable<ClaimDocumentationDocumentInfo[]> {
    return this.http.get<ClaimDocumentationDocumentInfo[]>(
      this.configService.PortalWebApiUrl +  `Document/ClaimDocumentation/${claimIdentifier}`);
  }

  public removeClaimDocumentation(documentInfoId:string ) {
    return this.http.delete(
      this.configService.PortalWebApiUrl +  `Document/ClaimDocumentation/${documentInfoId}`);
  }

  public getClaimPhotos(claimIdentifier:string, documentTypeId:number, referenceType = 0, referenceId = ''): Observable<ClaimPhotoDocumentInfo[]> {
    
    let url = this.configService.PortalWebApiUrl +  `Document/ClaimPhotos/${claimIdentifier}/${documentTypeId}`;
    if(referenceType > 0 && !!referenceId){
      url += `/${referenceType}/${referenceId}`;
    }

    return this.http.get<ClaimPhotoDocumentInfo[]>(url);
  }

  public getClaimMovies(claimIdentifier:string, documentTypeId:number, referenceType = 0, referenceId = ''): Observable<ClaimMovieDocumentInfo[]> {
    
    let url = this.configService.PortalWebApiUrl +  `Document/ClaimMovies/${claimIdentifier}/${documentTypeId}`;
    if(referenceType > 0 && !!referenceId){
      url += `/${referenceType}/${referenceId}`;
    }

    return this.http.get<ClaimMovieDocumentInfo[]>(url);
  }


  public removeBatchClaimPhoto(documentInfoIds:string[] ) {
    return this.http.post(
      this.configService.PortalWebApiUrl +  `Document/RemoveBatchClaimPhoto`, documentInfoIds);
  }

  public removeClaimPhoto(documentInfoIds:string[] ) {
    return this.http.delete(
      this.configService.PortalWebApiUrl +  `Document/ClaimPhoto`, { body: documentInfoIds});
  }

  public getSentDocuments(claimIdentifier:string ): Observable<SentDocumentInfo[]> {
    return this.http.get<SentDocumentInfo[]>(
      this.configService.PortalWebApiUrl +  `Document/SentDocuments/${claimIdentifier}`);
  }

  public searchDocumentsByIds(criteria:SearchDocumentsByIdsCriteria) : Observable<DocumentDetailedInfoSearchResultItem[]> {
    return this.http.post<DocumentDetailedInfoSearchResultItem[]>(
      this.configService.PortalWebApiUrl + 'Document/searchByIds', criteria);
  }

  public getExportDocumentsData(ids:string[]) : Observable<ExportDocumentsData> {

    const localizationCode = this.localizationManagerService.getCurrentLocalizationKey();

    return this.http.post<ExportDocumentsData>(
      this.configService.PortalWebApiUrl + 'Document/getExportDocumentsData/' + localizationCode, ids);
  }

  public exportDocumentsAsCompressedFile( items: ExportDocumentItem[]) {
    return this.http.post(`${this.configService.PortalWebApiUrl}Document/exportDocumentsAsCompressedFile`,items,
     { reportProgress: true, responseType: 'blob'} );
  }
  
  public exportDocumentsAsLink( data: ExportDocumentsAsLinkRequest) : Observable<string> {
    return this.http.post<string>(`${this.configService.PortalWebApiUrl}Document/exportDocumentsAsLink`,data);
  }

  public getDocumentsDataForExternalExport( linkAlias: string) : Observable<DocumentLinkInfo> {
    const localizationCode = this.localizationManagerService.getCurrentLocalizationKey();
    return this.http.get<DocumentLinkInfo>(
      this.configService.PortalWebApiUrl +  `DocumentExternal/${linkAlias}/${localizationCode}`);
  }

  public exportExternalDocumentsAsCompressedFile( items: ExportDocumentItem[]) {
    return this.http.post(`${this.configService.PortalWebApiUrl}DocumentExternal/exportDocumentsAsCompressedFile`,items,
     { reportProgress: true, responseType: 'blob'} );
  }

  public downloadExternalDocument( documentInfoId: string) {
    return this.http.get(`${this.configService.PortalWebApiUrl}DocumentExternal/Download/${documentInfoId}`,
     { reportProgress: true, responseType: 'blob'} );
  }

  public searchSharedClaimDocumentLinks(criteria:SearchSharedClaimDocumentLinkCriteria) : Observable<SharedClaimDocumentLinkSearchResultItem[]> {
    return this.http.post<SharedClaimDocumentLinkSearchResultItem[]>(
      this.configService.PortalWebApiUrl + 'Document/searchSharedClaimDocumentLinks', criteria);
  }

  public getSharedClaimDocumentLinkByLinkHash( linkHash: string) : Observable<SharedClaimDocumentLink> {
    return this.http.get<SharedClaimDocumentLink>(
      this.configService.PortalWebApiUrl +  `Document/getSharedClaimDocumentLinkByLinkHash/${linkHash}`);
  }

  public getSharedClaimDocumentLink( id: number) : Observable<SharedClaimDocumentLink> {
    return this.http.get<SharedClaimDocumentLink>(
      this.configService.PortalWebApiUrl +  `Document/getSharedClaimDocumentLink/${id}`);
  }

  public createSharedClaimDocumentLink(sharedClaimDocumentLink:SharedClaimDocumentLink) : Observable<number> {
    return this.http.post<number>(
      this.configService.PortalWebApiUrl + 'Document/createSharedClaimDocumentLink', sharedClaimDocumentLink);
  }

}
